<template>
  <div class="form-container">
    <div>
      <div class="d-flex flex-row justify-content-between mb-3">
        <div></div>
        <div class="d-flex flex-column">
          <span class="text-center">Status</span>
          <div class="status-detail"
               :class="statusChange(dataSurvey)"
               size="sm"
               disabled
          >
            {{ dataSurvey ? dataSurvey.status : '-' }}
          </div>
        </div>
      </div>
      <div class="d-flex flex-row justify-content-between wrapper p-3 mb-3">
        <div class="align-items-center">
          <span class="text-file-name"><b>Period : </b>{{ dataSurvey ? dataSurvey.periode_doc : '-' }}</span>
        </div>
        <div class="align-self-center">
          <span class="text-file-name"><b>Upload Date : </b>{{ dataSurvey ? dataSurvey.created_at : '-' }}</span>
        </div>
      </div>
    </div>
    <b-table
      hover
      class="mb-4"
      table-class="table-summary-progress"
      thead-class="bg-color"
      empty-text="No data"
      ref="summaryProgressDetailDataOnline"
      :items="dataSurvey.items"
      :fields="fields"
      sticky-header="400px"
      sort-icon-left
      show-empty
      responsive
    >
      <template v-slot:table-busy>
        <div class="text-center my-4">
          <b-spinner variant="info" class="spinner-lg" />
        </div>
      </template>
      <template v-slot:cell()="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <b-form-input v-else v-model="item.temp[key]" size="sm" />
      </template>
      <template v-slot:cell(uploaded_at)="{ value, item }">
        <template v-if="!item.editing">{{ item.uploaded_at }}</template>
        <b-form-input v-else :value="item.uploaded_at" size="sm" disabled />
      </template>
      <template v-slot:cell(contractor_id)="{ value, item }">
        <template v-if="!item.editing">{{ item.Contractor.code }}</template>
        <b-form-input v-else :value="item.Contractor.code" size="sm" disabled />
      </template>
      <template v-slot:cell(contract_type)="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <b-form-input v-else v-model="item.temp[key]" size="sm" disabled />
      </template>
      <template v-slot:cell(type_id)="{ value, item }">
        <template v-if="!item.editing">{{ item.RateType.type_name }}</template>
        <b-form-input v-else :value="item.RateType.type_name" size="sm" disabled />
      </template>
      <template v-slot:cell(volume)="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <b-form-input v-else v-model="item.temp[key]" size="sm" type="number" />
      </template>
      <template v-slot:cell(distance)="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <b-form-input v-else v-model="item.temp[key]" size="sm" type="number" />
      </template>
      <template v-slot:cell(unit)="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <b-form-input v-else v-model="item.temp[key]" size="sm" disabled />
      </template>
      <template v-slot:cell(payment_status)="{ value, item, field: { key }}">
        <template v-if="!item.editing">{{ value }}</template>
        <v-select
          v-else
          v-model="item.temp[key]"
          placeholder="Select Payment Status"
          :options="paymentStatusOption"
          size="sm"
        />
      </template>
      <template v-slot:cell(actions)="items">
        <b-dropdown
          toggle-class="text-dark p-0"
          menu-class="shadow border-0"
          variant="link"
          v-if="!items.item.editing"
          right
          no-caret
        >
          <template v-slot:button-content>
            <fa-icon class="text-info" icon="ellipsis-v" />
          </template>
          <b-dropdown-item
            v-if="canEdit && !items.item.editing && (dataSurvey.status === 'Draft' || $route.query.id)"
            @click="doEdit(items.item)"
          >
            Edit
          </b-dropdown-item>
          <b-dropdown-item
            variant="danger"
            v-if="(canDelete && !items.item.editing) && !$route.query.id"
            @click="doDelete(items.item.id)"
          >
            Delete
          </b-dropdown-item>
        </b-dropdown>
        <div class="d-flex flex-row">
          <img v-if="items.item.editing" @click="doCancel(items.item)"
               :src="require('@/assets/icon-cancel.png')"
               width="20" class="pt-2 mr-2"
          >
          <img v-if="items.item.editing" @click="doSave(items.item)"
               :src="require('@/assets/icon-submit.png')"
               width="20" class="pt-2"
          >
        </div>
      </template>
    </b-table>
    <div class="d-flex justify-content-end align-items-center mt-5">
      <b-button class="btn-reset" variant="outline-info" @click="onBack">
        Cancel
      </b-button>
      <b-button
        class="btn-custom ml-2"
        variant="info"
        @click="onConfirm"
        v-if="items.status !== 'Draft'"
        :disable="isBusy"
      >
        Submit
      </b-button>
    </div>

    <modal-delete @click="onDelete" :message="text" />
    <modal-failed :message="textError" />
    <modal-successful :message="text" />
    <modal-approve @submit="onSubmit" :text="text" />
  </div>
</template>

<script>
import api from '@/api'
import { hasPermission } from '@/utils/permissions'
import {
  SUMMARY_PROGRESS_DATA_SURVEY_DELETE,
  SUMMARY_PROGRESS_DATA_SURVEY_EDIT,
  SUMMARY_PROGRESS_DATA_SURVEY_ADD
} from '@/fixtures'
import ModalFailed from './ModalFailed'
import ModalSuccessful from '@/partials/ModalSuccessful'
import ModalDelete from '@/partials/ModalDelete'
import ModalApprove from '@/partials/ModalApprove'

export default {
  components: {
    ModalDelete,
    ModalFailed,
    ModalSuccessful,
    ModalApprove
  },
  data: () => ({
    canEdit: hasPermission(SUMMARY_PROGRESS_DATA_SURVEY_EDIT),
    canDelete: hasPermission(SUMMARY_PROGRESS_DATA_SURVEY_DELETE),
    canSubmit: hasPermission(SUMMARY_PROGRESS_DATA_SURVEY_ADD),
    isBusy: false,
    text: null,
    textError: {
      message: 'T',
      errors: 'Theres something wrong while updating/deleting',
      code: '',
      stack: ''
    },
    dataSurvey: {
      items: []
    },
    selectedId: null,
    items: [],
    contractorOption: [],
    typeOption: [],
    paymentStatusOption: ['Pay', 'Pending', 'No Pay'],
    fields: [
      { key: 'actions', sortable: false, tdClass: 'text-center', thClass: 'start', thStyle: 'width: 50px' },
      { key: 'uploaded_at', label: 'Uploaded At', sortable: true },
      { key: 'contractor_id', label: 'Contractor' },
      { key: 'contract_type', label: 'Type Contract' },
      { key: 'pit', label: 'PIT', editable: true },
      { key: 'type_id', label: 'Type' },
      { key: 'detail', label: 'Detail', editable: true },
      { key: 'volume', label: 'Volume', editable: true },
      { key: 'unit', label: 'Unit', editable: true },
      { key: 'destination', label: 'Destination', editable: true },
      { key: 'distance', label: 'Distance', editable: true },
      { key: 'payment_status', label: 'Payment Status', editable: true },
      { key: 'description', label: 'Description', thClass: 'end', editable: true }
    ]
  }),
  mounted() {
    this.init()
    this.fetchDetailSurvey()
    this.fetchRateType()
  },
  methods: {
    async init() {
      this.idEdit = this.$route.params.id
    },
    async fetchDetailSurvey() {
      this.dataSurvey = await api.summaryProgressDataOnline.get(this.idEdit)
      if (this.$route.query.from === 'summary-resume-volume-work') {
        this.dataSurvey.items = this.dataSurvey.items.filter(v => v.payment_status === 'Pay')
      }
      this.items = this.dataSurvey.items
      this.items.map((item) => {
        item.contractor_id = item.Contractor.id
        item.contract_type = item.Contractor.contract_type
        item.type_id = item.RateType.id
      })
    },
    async fetchRateType() {
      this.typeOption = await api.contractor.rateType()
    },
    setTypeContract(data) {
      data.contract_type = this.contractorOption.filter(v => v.id === data.contractor_id)[0].type
    },
    setUnit(data) {
      data.unit = this.typeOption.filter(v => v.id === data.type_id)[0].unit
    },
    onBack() {
      if (this.$route.query.from === 'summary-volume-calculation') {
        this.$router.push({ path: '/summary-volume-calculation' })
      } else {
        this.$router.push({ path: '/summary-progress-data-survey' })
      }
    },
    doEdit(item) {
      this.$set(item, 'temp', JSON.parse(JSON.stringify(item)))
      this.$set(item, 'editing', true)
    },
    doCancel(item) {
      this.$set(item, 'editing', false)
    },
    async doSave(item) {
      this.$set(item, 'editing', false)
      try {
        await api.summaryProgressDataOnline.saveDetail(item)
        this.text = 'edit summary survey'
        this.$bvModal.show('modal-successful')
      } catch ({ response }) {
        this.textError = response.data.error
        this.$bvModal.show('modal-failed')
      } finally {
        this.fetchDetailSurvey()
      }
    },
    async doDelete(item) {
      this.selectedId = item
      this.$bvModal.show('modal-delete')
    },
    async onDelete() {
      try {
        await api.summaryProgressDataOnline.removeDetailSurvey(this.selectedId)
        this.text = 'delete summary survey'
        this.$bvModal.show('modal-successful')
      } catch ({ response }) {
        this.textError = response.data.error
        this.$bvModal.show('modal-failed')
      } finally {
        this.fetchDetailSurvey()
      }
    },
    onConfirm() {
      this.text = 'Submit this document'
      this.$bvModal.show('modal-approve')
    },
    async onSubmit() {
      try {
        this.isBusy = !this.isBusy
        if (this.$route.query.from === 'summary-volume-calculation') {
          await api.summaryVolumeCalculation.submit(this.$route.query.id)
          this.text = 'Submit perhitungan bersama'
          this.$bvModal.show('modal-successful')
          this.$router.push({ path: '/summary-volume-calculation' })
          this.isBusy = !this.isBusy
        } else if (this.$route.query.from === 'summary-resume-volume-work') {
          await api.summaryResumeVolumeWork.submit(this.$route.query.id)
          this.text = 'Submit kemajuan pekerjaan'
          this.$bvModal.show('modal-successful')
          this.$router.push({ path: '/summary-resume-volume-work' })
          this.isBusy = !this.isBusy
        } else {
          await api.summaryProgressDataOnline.submitStatus(this.idEdit)
          this.text = 'Submit summary survey'
          this.$bvModal.show('modal-successful')
          this.$router.push({ path: '/summary-progress-data-survey' })
          this.isBusy = !this.isBusy
        }
      } catch ({ response }) {
        this.isBusy = !this.isBusy
        this.textError = response.data.error
        this.$bvModal.show('modal-failed')
      }
    },
    statusChange(item) {
      if (item?.status === 'Draft') {
        return 'draft'
      } else if (item?.status === 'Approved') {
        return 'approved'
      } else if (item?.status === 'Revise') {
        return 'revised'
      } else if (item?.status === 'Waiting Approval') {
        return 'waiting-approval'
      } else {
        return 'waiting-approval'
      }
    }
  }
}
</script>

<style lang="scss" scoped>

@media (min-width: 576px){
  /deep/.modal-dialog {
    max-width: 80%;
  }
}

.text-file-name{
  font-weight: 200;
  font-size:16px;
}

.table {
  width: 100%;
}

.text-size{
  font-weight:200;
  font-size:18px;
  color: #3cbdb1;
}

.wrapper {
  background-color: #DBF9F6;
  border-radius: 16px;
}

.status-detail {
  font-size: 14px;
  min-width: 100px;
  max-width:200px;
}

.mb-3 {
  margin-bottom: 0.5rem !important;
}
</style>
